/**
 * Имена локальных баз доски и уборка старых.
 *
 * Отдельный модуль от `lesson-doc.ts` намеренно: там Yjs, HocuspocusProvider и
 * y-indexeddb, то есть сотни килобайт, которые обязаны ехать ленивым чанком
 * комнаты. Уборку же зовёт список уроков, а он в стартовом чанке. Один импорт
 * оттуда в lesson-doc.ts утащил бы всю доску в старт, и при 128 кбит/с это
 * разница между «открылось» и «минута ожидания» (раздел 3.1 CLAUDE.md).
 */

/**
 * Префиксов ДВА, и это не украшение адресной строки IndexedDB.
 *
 * База именуется по ДОКУМЕНТУ, а не по уроку: у занятия группы уроков N, а
 * документ один и назван сеансом (`boardIdOf` в `@edu/shared`). Значит имена
 * бывают двух видов, и уборщик обязан их различать: сеансовое имя, посчитанное
 * по списку уроков, никогда не совпадёт с урочным, и одним префиксом он снёс бы
 * ровно те базы, которые держат общую доску занятия.
 */
const LESSON_PREFIX = "edu-lesson-";
const SESSION_PREFIX = "edu-session-";

/** База занятия 1:1: документ на урок, значит и база на урок. */
export function lessonDbName(lessonId: string): string {
  return `${LESSON_PREFIX}${lessonId}`;
}

/** База занятия группы: документ на сеанс, значит и база на сеанс. */
export function sessionDbName(sessionId: string): string {
  return `${SESSION_PREFIX}${sessionId}`;
}

/**
 * Что оставить на устройстве.
 *
 * ⚠️ **`sessions === null` означает «сеансовые базы не трогаем вовсе», и это не
 * то же самое, что пустой список.** Пустой список это «сеансов у меня нет,
 * сеансовые базы можно стирать все»; `null` это «я не знаю, какие есть», и тогда
 * уборщик обязан пройти мимо. Разница появляется ровно в одном случае: новый
 * бандл против сервера, который поля `groupSessionId` ещё не отдаёт. Список при
 * этом выглядит законным и полным, а стёр бы он базу с несинхронизированной
 * работой ребёнка.
 */
export interface BoardDbPlan {
  lessons: readonly string[];
  sessions: readonly string[] | null;
}

/**
 * План уборки по списку уроков, который отдал сервер.
 *
 * ⚠️ **Поле отсутствует хоть у одного урока это отказ от чистки сеансовых баз
 * ЦЕЛИКОМ**, а не пропуск одной строки. Это то же правило, что «никогда не
 * удалять имя, которое не смог классифицировать», только на уровень выше:
 * неполный список сеансов неотличим от полного, и вреда от него ровно столько
 * же, сколько от неузнанного имени.
 */
export function planBoardDatabases(
  lessons: readonly { id: string; groupSessionId?: string | null | undefined }[],
): BoardDbPlan {
  const sessions: string[] = [];
  let complete = true;
  for (const lesson of lessons) {
    if (lesson.groupSessionId === undefined) complete = false;
    else if (lesson.groupSessionId !== null) sessions.push(lesson.groupSessionId);
  }
  return {
    lessons: lessons.map((l) => l.id),
    sessions: complete ? sessions : null,
  };
}

/**
 * Какие из этих имён подлежат удалению.
 *
 * Вынесено отдельной чистой функцией от самой уборки, потому что решение здесь, а
 * `indexedDB.deleteDatabase` только исполняет. Проверять надо решение.
 *
 * Правило неизменно: **никогда не удаляем имя, которое не смогли
 * классифицировать.** Чужой префикс это чужая база (на том же домене живёт
 * `y-indexeddb` кого угодно), незнакомое имя это либо будущая наша, либо не наша
 * вовсе, и в обоих случаях ошибка стоит дороже занятого места.
 */
export function boardDatabasesToDelete(
  names: readonly (string | undefined)[],
  plan: BoardDbPlan,
): string[] {
  const keepLessons = new Set(plan.lessons.map(lessonDbName));
  const keepSessions = plan.sessions === null ? null : new Set(plan.sessions.map(sessionDbName));

  const doomed: string[] = [];
  for (const name of names) {
    if (name === undefined) continue;
    if (name.startsWith(LESSON_PREFIX)) {
      if (!keepLessons.has(name)) doomed.push(name);
      continue;
    }
    if (name.startsWith(SESSION_PREFIX)) {
      // Список неполон: сеансовые базы не наше дело до следующего захода
      if (keepSessions !== null && !keepSessions.has(name)) doomed.push(name);
    }
  }
  return doomed;
}

/**
 * Удаляет базы досок, которых больше нет в списке уроков.
 *
 * Зачем вообще: база живёт на устройстве вечно, а уроков за год у активного
 * преподавателя сотни. Убранный в корзину или чужой урок не должен занимать
 * место и уж тем более оставлять содержимое доски на устройстве, с которого
 * ученика отвязали.
 *
 * Список уроков это источник правды: сервер уже применил правила доступа, и
 * всё, чего в ответе нет, для этого пользователя не существует.
 */
export async function pruneBoardDatabases(
  lessons: readonly { id: string; groupSessionId?: string | null | undefined }[],
): Promise<void> {
  // indexedDB.databases() нет в Firefox и в Safari до 14: там уборка молча не
  // происходит, и это лучше, чем перебирать имена наугад. Удалять базу, о
  // которой мы не знаем, что она наша, нельзя
  if (typeof indexedDB === "undefined" || typeof indexedDB.databases !== "function") return;

  let names: (string | undefined)[];
  try {
    names = (await indexedDB.databases()).map((d) => d.name);
  } catch {
    // Приватный режим и заблокированное хранилище: уборка не критична
    return;
  }

  for (const name of boardDatabasesToDelete(names, planBoardDatabases(lessons))) {
    // Открытая где-то ещё база не удалится, а повиснет в состоянии blocked.
    // Ничего не ждём и ничем не отвечаем: следующий заход уберёт её
    indexedDB.deleteDatabase(name);
  }
}
